"use client";

import * as React from "react";
import type { HTMLAttributes } from "react";
import { useId } from "react";

import { cn } from "../../lib/utils";

/**
 * The Caruso brand spinner.
 *
 * Vendored from the design asset (`Group 1000006341.svg`) with its path data
 * untouched. The dots are slightly ELLIPTICAL — the artboard is 38.84 x 39.41,
 * not square — so they are kept as paths rather than rewritten as circles,
 * which would round them and change the mark.
 *
 * Three rings, outward: the brand violet at the centre, a mid tone, then the
 * lightest at the rim. Colours are fixed rather than `currentColor`, because
 * this is a brand mark; a `text-*` class on it no longer tints it.
 */

const SpinnerMark = ({
	size,
	titleId,
	clipId,
}: {
	size: number;
	titleId: string;
	clipId: string;
}) => (
	<svg
		aria-labelledby={titleId}
		fill="none"
		height={size}
		role="img"
		viewBox="0 0 39 40"
		width={size}
	>
		<title id={titleId}>Loading</title>
		<g clipPath={`url(#${clipId})`}>
			<path
				d="M19.4196 24.2065C21.8702 24.2065 23.8568 22.1907 23.8568 19.7041C23.8568 17.2175 21.8702 15.2017 19.4196 15.2017C16.969 15.2017 14.9824 17.2175 14.9824 19.7041C14.9824 22.1907 16.969 24.2065 19.4196 24.2065Z"
				fill="#7F6DF1"
			/>
			<path
				d="M19.4187 12.2089C21.0566 12.2089 22.3843 10.8616 22.3843 9.19965C22.3843 7.5377 21.0566 6.19043 19.4187 6.19043C17.7809 6.19043 16.4531 7.5377 16.4531 9.19965C16.4531 10.8616 17.7809 12.2089 19.4187 12.2089Z"
				fill="#AFA3FE"
			/>
			<path
				d="M19.4194 3.74065C20.4374 3.74065 21.2626 2.90327 21.2626 1.87032C21.2626 0.837372 20.4374 0 19.4194 0C18.4014 0 17.5762 0.837372 17.5762 1.87032C17.5762 2.90327 18.4014 3.74065 19.4194 3.74065Z"
				fill="#D2CBFF"
			/>
			<path
				d="M19.4194 39.4096C20.4374 39.4096 21.2626 38.5722 21.2626 37.5393C21.2626 36.5063 20.4374 35.6689 19.4194 35.6689C18.4014 35.6689 17.5762 36.5063 17.5762 37.5393C17.5762 38.5722 18.4014 39.4096 19.4194 39.4096Z"
				fill="#D2CBFF"
			/>
			<path
				d="M12.6928 5.09856C13.7108 5.09856 14.536 4.26118 14.536 3.22823C14.536 2.19528 13.7108 1.35791 12.6928 1.35791C11.6748 1.35791 10.8496 2.19528 10.8496 3.22823C10.8496 4.26118 11.6748 5.09856 12.6928 5.09856Z"
				fill="#D2CBFF"
			/>
			<path
				d="M26.146 38.0522C27.1639 38.0522 27.9892 37.2148 27.9892 36.1818C27.9892 35.1489 27.1639 34.3115 26.146 34.3115C25.128 34.3115 24.3027 35.1489 24.3027 36.1818C24.3027 37.2148 25.128 38.0522 26.146 38.0522Z"
				fill="#D2CBFF"
			/>
			<path
				d="M6.99166 8.96428C8.00964 8.96428 8.83487 8.12691 8.83487 7.09396C8.83487 6.06101 8.00964 5.22363 6.99166 5.22363C5.97367 5.22363 5.14844 6.06101 5.14844 7.09396C5.14844 8.12691 5.97367 8.96428 6.99166 8.96428Z"
				fill="#D2CBFF"
			/>
			<path
				d="M31.8471 34.186C32.8651 34.186 33.6903 33.3486 33.6903 32.3156C33.6903 31.2827 32.8651 30.4453 31.8471 30.4453C30.8291 30.4453 30.0039 31.2827 30.0039 32.3156C30.0039 33.3486 30.8291 34.186 31.8471 34.186Z"
				fill="#D2CBFF"
			/>
			<path
				d="M3.18111 14.7499C4.19909 14.7499 5.02433 13.9126 5.02433 12.8796C5.02433 11.8466 4.19909 11.0093 3.18111 11.0093C2.16313 11.0093 1.33789 11.8466 1.33789 12.8796C1.33789 13.9126 2.16313 14.7499 3.18111 14.7499Z"
				fill="#D2CBFF"
			/>
			<path
				d="M35.6577 28.4003C36.6757 28.4003 37.5009 27.5629 37.5009 26.53C37.5009 25.497 36.6757 24.6597 35.6577 24.6597C34.6397 24.6597 33.8145 25.497 33.8145 26.53C33.8145 27.5629 34.6397 28.4003 35.6577 28.4003Z"
				fill="#D2CBFF"
			/>
			<path
				d="M1.84322 21.5746C2.8612 21.5746 3.68643 20.7373 3.68643 19.7043C3.68643 18.6714 2.8612 17.834 1.84322 17.834C0.825236 17.834 0 18.6714 0 19.7043C0 20.7373 0.825236 21.5746 1.84322 21.5746Z"
				fill="#D2CBFF"
			/>
			<path
				d="M36.9956 21.5746C38.0135 21.5746 38.8388 20.7373 38.8388 19.7043C38.8388 18.6714 38.0135 17.834 36.9956 17.834C35.9776 17.834 35.1523 18.6714 35.1523 19.7043C35.1523 20.7373 35.9776 21.5746 36.9956 21.5746Z"
				fill="#D2CBFF"
			/>
			<path
				d="M3.18111 28.4003C4.19909 28.4003 5.02433 27.5629 5.02433 26.53C5.02433 25.497 4.19909 24.6597 3.18111 24.6597C2.16313 24.6597 1.33789 25.497 1.33789 26.53C1.33789 27.5629 2.16313 28.4003 3.18111 28.4003Z"
				fill="#D2CBFF"
			/>
			<path
				d="M35.6577 14.7499C36.6757 14.7499 37.5009 13.9126 37.5009 12.8796C37.5009 11.8466 36.6757 11.0093 35.6577 11.0093C34.6397 11.0093 33.8145 11.8466 33.8145 12.8796C33.8145 13.9126 34.6397 14.7499 35.6577 14.7499Z"
				fill="#D2CBFF"
			/>
			<path
				d="M6.99166 34.186C8.00964 34.186 8.83487 33.3486 8.83487 32.3156C8.83487 31.2827 8.00964 30.4453 6.99166 30.4453C5.97367 30.4453 5.14844 31.2827 5.14844 32.3156C5.14844 33.3486 5.97367 34.186 6.99166 34.186Z"
				fill="#D2CBFF"
			/>
			<path
				d="M31.8471 8.96428C32.8651 8.96428 33.6903 8.12691 33.6903 7.09396C33.6903 6.06101 32.8651 5.22363 31.8471 5.22363C30.8291 5.22363 30.0039 6.06101 30.0039 7.09396C30.0039 8.12691 30.8291 8.96428 31.8471 8.96428Z"
				fill="#D2CBFF"
			/>
			<path
				d="M12.6928 38.0522C13.7108 38.0522 14.536 37.2148 14.536 36.1818C14.536 35.1489 13.7108 34.3115 12.6928 34.3115C11.6748 34.3115 10.8496 35.1489 10.8496 36.1818C10.8496 37.2148 11.6748 38.0522 12.6928 38.0522Z"
				fill="#D2CBFF"
			/>
			<path
				d="M26.146 5.09856C27.1639 5.09856 27.9892 4.26118 27.9892 3.22823C27.9892 2.19528 27.1639 1.35791 26.146 1.35791C25.128 1.35791 24.3027 2.19528 24.3027 3.22823C24.3027 4.26118 25.128 5.09856 26.146 5.09856Z"
				fill="#D2CBFF"
			/>
			<path
				d="M19.4187 33.2191C21.0566 33.2191 22.3843 31.8719 22.3843 30.2099C22.3843 28.548 21.0566 27.2007 19.4187 27.2007C17.7809 27.2007 16.4531 28.548 16.4531 30.2099C16.4531 31.8719 17.7809 33.2191 19.4187 33.2191Z"
				fill="#AFA3FE"
			/>
			<path
				d="M12.1004 15.2865C13.7382 15.2865 15.066 13.9392 15.066 12.2773C15.066 10.6153 13.7382 9.26807 12.1004 9.26807C10.4625 9.26807 9.13477 10.6153 9.13477 12.2773C9.13477 13.9392 10.4625 15.2865 12.1004 15.2865Z"
				fill="#AFA3FE"
			/>
			<path
				d="M26.739 30.1415C28.3769 30.1415 29.7047 28.7942 29.7047 27.1323C29.7047 25.4703 28.3769 24.123 26.739 24.123C25.1012 24.123 23.7734 25.4703 23.7734 27.1323C23.7734 28.7942 25.1012 30.1415 26.739 30.1415Z"
				fill="#AFA3FE"
			/>
			<path
				d="M9.06717 22.7138C10.705 22.7138 12.0328 21.3665 12.0328 19.7045C12.0328 18.0426 10.705 16.6953 9.06717 16.6953C7.42931 16.6953 6.10156 18.0426 6.10156 19.7045C6.10156 21.3665 7.42931 22.7138 9.06717 22.7138Z"
				fill="#AFA3FE"
			/>
			<path
				d="M29.7723 22.7138C31.4101 22.7138 32.7379 21.3665 32.7379 19.7045C32.7379 18.0426 31.4101 16.6953 29.7723 16.6953C28.1344 16.6953 26.8066 18.0426 26.8066 19.7045C26.8066 21.3665 28.1344 22.7138 29.7723 22.7138Z"
				fill="#AFA3FE"
			/>
			<path
				d="M12.1004 30.1415C13.7382 30.1415 15.066 28.7942 15.066 27.1323C15.066 25.4703 13.7382 24.123 12.1004 24.123C10.4625 24.123 9.13477 25.4703 9.13477 27.1323C9.13477 28.7942 10.4625 30.1415 12.1004 30.1415Z"
				fill="#AFA3FE"
			/>
			<path
				d="M26.739 15.2865C28.3769 15.2865 29.7047 13.9392 29.7047 12.2773C29.7047 10.6153 28.3769 9.26807 26.739 9.26807C25.1012 9.26807 23.7734 10.6153 23.7734 12.2773C23.7734 13.9392 25.1012 15.2865 26.739 15.2865Z"
				fill="#AFA3FE"
			/>
		</g>
		<defs>
			<clipPath id={clipId}>
				<rect width="38.8385" height="39.4097" fill="white" />
			</clipPath>
		</defs>
	</svg>
);

export type ChatLoaderProps = HTMLAttributes<HTMLDivElement> & {
	size?: number;
	/**
	 * Seconds per full turn.
	 *
	 * The brand mark turns slowly and linearly. Tailwind's `animate-spin` is 1s,
	 * which reads as frantic on a 25-dot mandala, so the duration is overridden
	 * inline and exposed as a prop.
	 */
	durationSeconds?: number;
};

/**
 * The spinner used inside a transcript.
 *
 * Named apart from the design system's `Spinner` because it is a different mark
 * and both need to exist while the two surfaces still differ.
 */
/**
 * The size the transcript shows the mark at while the agent is working.
 *
 * Shared so every row that carries the mark agrees: they replace one another
 * across a turn, and any two that differ make the mark visibly resize partway
 * through. That has now happened twice — first the pending row against the
 * reasoning trigger at 2px apart, then the app's own status rows at 14px
 * against this file's 18px, which is a third of the mark.
 *
 * Use `ChatStatusRow` rather than this constant directly. The size alone was not
 * enough: the drift returned because the ROW was duplicated, not just the number.
 */
export const THINKING_MARK_SIZE = 18;

export const ChatLoader = ({
	className,
	size = THINKING_MARK_SIZE,
	durationSeconds = 3.5,
	...props
}: ChatLoaderProps) => {
	const id = useId();

	return (
		<div
			className={cn("inline-flex items-center justify-center", className)}
			{...props}
		>
			<span
				className="inline-flex animate-spin motion-reduce:animate-none"
				style={{ animationDuration: `${durationSeconds}s` }}
			>
				<SpinnerMark
					clipId={`${id}-clip`}
					size={size}
					titleId={`${id}-title`}
				/>
			</span>
		</div>
	);
};

export type ChatStatusRowProps = HTMLAttributes<HTMLDivElement> & {
	/** The line itself — "Thinking...", "Reconnecting...", whatever the turn is doing. */
	label: React.ReactNode;
	/**
	 * Whether the spinning mark leads the row.
	 *
	 * Off for a row that is not waiting on anything: an approval gate has stopped
	 * the run, so a spinner there would claim work is happening when it is not.
	 */
	showMark?: boolean;
};

/**
 * One line of transcript status: the mark, then what is happening.
 * Anything that renders a status line in a transcript renders this, so the two
 * halves of that handover cannot drift again.
 */
export const ChatStatusRow = ({
	label,
	showMark = true,
	className,
	...props
}: ChatStatusRowProps) => (
	<div
		className={cn("flex items-center gap-2 text-neutral-500", className)}
		{...props}
	>
		{showMark && <ChatLoader size={THINKING_MARK_SIZE} />}
		<span className="text-sm">{label}</span>
	</div>
);
